// src/components/About.js
import React from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import AboutPic from '../images/AboutPic.png';

const AboutContainer = styled.section`
  background: linear-gradient(5deg, 
    ${props => props.theme.colors.primary}, 
    ${props => props.theme.colors.secondary}
  );
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: white;
  padding: ${props => props.theme.spacing.xxl} ${props => props.theme.spacing.xl};
`;

const ContentWrapper = styled(motion.div)`
  max-width: 1200px;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 4rem;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: center;
    gap: 2rem;
  }
`;

const ImageSection = styled(motion.div)`
  flex: 1;
`;

const ProfileImage = styled.img`
  width: 100%;
  max-width: 400px;
  height: auto;
  border-radius: 10px;
  object-fit: cover;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
`;

const TextSection = styled(motion.div)`
  flex: 2;
`;

const Title = styled(motion.h1)`
  font-size: 48px;
  margin-bottom: ${props => props.theme.spacing.lg};
  background: linear-gradient(5deg, 
    ${props => props.theme.colors.primaryLight}, 
    white
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled(motion.h2)`
  color: ${props => props.theme.colors.primaryLight};
  margin-bottom: ${props => props.theme.spacing.md};
  font-size: 24px;
`;

const Content = styled(motion.p)`
  margin-bottom: ${props => props.theme.spacing.md};
  line-height: 1.6;
  font-size: 18px;
  opacity: 0.9;
`;

const Quote = styled(motion.blockquote)`
  background-color: rgba(255, 255, 255, 0.1);
  padding: ${props => props.theme.spacing.lg};
  border-radius: 10px;
  margin-top: ${props => props.theme.spacing.xl};
  font-style: italic;
  font-size: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const About = () => {
  return (
    <AboutContainer>
      <ContentWrapper
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <ImageSection
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <ProfileImage src={AboutPic} alt="Zoe Technologies" />
        </ImageSection>
        <TextSection
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Title>About Me</Title>
          <Subtitle>Tech Consultant & Strategist</Subtitle>
          <Content>
            Hi, I'm Elva Osorio—a passionate tech consultant with a background in software engineering,
            product management, and data analysis. I specialize in helping businesses bridge the gap between
            technology and strategy.
          </Content>
          <Content>
            My expertise spans AI solutions, analytics, and tailored digital strategies. I've collaborated with 
            startups, nonprofits, and enterprise teams to streamline operations and deliver impactful results.
          </Content>
          <Content>
            When I'm not consulting, you'll find me exploring AI trends or teaching others how to embrace 
            technology with confidence. I'm also actively involved in organizations like Girls Who Code,
            Latinas in Tech, and Lesbians Who Tech.
          </Content>
          <Quote
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            "Empowering small businesses and nonprofits to thrive in a digital world, fostering resilience and growth."
          </Quote>
        </TextSection>
      </ContentWrapper>
    </AboutContainer>
  );
};

export default About;
